// src/stores/auth.ts — session state. The permission list here decides only what is
// RENDERED; the backend re-checks every request, so this is convenience, not security.

import { create } from 'zustand';
import { api, tokenStore } from '../lib/api';
import { clearIntro } from '../lib/appReady';
import type { AuthUser } from '../types/api';

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  /** Keep a session another sign-in path returned (the email code → set password flow): tokens stored, user set. */
  startSession: (session: { accessToken: string; refreshToken: string; user: AuthUser }) => void;
  logout: () => Promise<void>;
  restore: () => Promise<void>;
  /** Change your own password (current may be omitted straight after an administrator's reset). */
  changePassword: (current: string | null, next: string) => Promise<void>;
  /** Does the signed-in user hold "page.action"? */
  can: (permission: string) => boolean;
  /** Any action at all on this page — used to decide whether to show a nav item. */
  canAccessPage: (page: string) => boolean;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  loading: true,
  error: null,

  async login(email, password) {
    set({ loading: true, error: null });
    try {
      const { data } = await api.post<{ accessToken: string; refreshToken: string; user: AuthUser }>(
        '/auth/login',
        { email, password },
      );
      tokenStore.set({ accessToken: data.accessToken, refreshToken: data.refreshToken });
      set({ user: data.user, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Sign in failed' });
      throw err;
    }
  },

  startSession(session) {
    tokenStore.set({ accessToken: session.accessToken, refreshToken: session.refreshToken });
    set({ user: session.user, loading: false, error: null });
  },

  async changePassword(current, next) {
    const { data } = await api.post<AuthUser>('/auth/change-password', { current: current || null, new: next });
    set({ user: data });
  },

  async logout() {
    try { await api.post('/auth/logout'); } catch { /* the session may already be gone */ }
    tokenStore.clear();
    clearIntro();
    set({ user: null });
  },

  async restore() {
    if (!tokenStore.get()?.accessToken) { set({ loading: false, user: null }); return; }
    try {
      const { data } = await api.get<AuthUser>('/auth/me');
      set({ user: data, loading: false });
    } catch {
      tokenStore.clear();
      set({ user: null, loading: false });
    }
  },

  can(permission) {
    const user = get().user;
    if (!user) return false;
    if (user.allAccess) return true;
    return user.permissions.includes(permission);
  },

  canAccessPage(page) {
    const user = get().user;
    if (!user) return false;
    if (user.allAccess) return true;
    return user.permissions.some((p) => p.startsWith(`${page}.`));
  },
}));
